import React, { useState, useRef } from 'react';
import Container from '../Container/Container';
import { useSelector } from 'react-redux';
import { post } from '../ApiServices/ApiServices';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt } from 'react-icons/fa';
import DateRangePicker from 'react-bootstrap-daterangepicker';

// Helper
// Get last Saturday
function getPriorSaturday() {
    const today = moment();
    const day = today.day();
    const diff = day === 6 ? 7 : day + 1;
    return today.clone().subtract(diff, 'days');
}

// Get 1 month before selected Saturday
function getStartDate(endDate) {
    return moment(endDate, 'MM/DD/YYYY').subtract(1, 'month');
}

function ProcessReturns() {
    const navigate = useNavigate();
    const profile = useSelector((state) => state.profile.profile);

    const [weekEndingDate, setWeekEndingDate] = useState(
        getPriorSaturday().format('MM/DD/YYYY')
    );

    const [startDate, setStartDate] = useState(
        getStartDate(getPriorSaturday().format('MM/DD/YYYY')).format('MM/DD/YYYY')
    );
    const [startRange, setStartRange] = useState({
        startDate: null,
        endDate: null
    });
    const [startLabel, setStartLabel] = useState('');

    const [weekRange, setWeekRange] = useState({
        startDate: null,
        endDate: null
    });
    const [weekLabel, setWeekLabel] = useState('');
    const [isStartEdited, setIsStartEdited] = useState(false);
    const [dateError, setDateError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const [history, setHistory] = useState([
        {
            processedAt: moment('2026-03-27 18:00', 'YYYY-MM-DD HH:mm'),
            total: 600,
            returned: 595,
            notReturned: 5,
        },
        {
            processedAt: moment('2026-03-20 17:00', 'YYYY-MM-DD HH:mm'),
            total: 500,
            returned: 490,
            notReturned: 10,
        },
    ]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // ✅ ADD HERE (inside component)
    const handleEndDateChange = (value) => {
        setWeekEndingDate(value);

        const parsed = moment(value, 'MM/DD/YYYY', true);
        if (parsed.isValid()) {
            const newStart = parsed.clone().subtract(10, 'month');
            setStartDate(newStart.format('MM/DD/YYYY'));
        }
    };

    const handleStartDateChange = (value) => {
        setStartDate(value);
        setIsStartEdited(true);
    };
    const confirmModalCloseRef = useRef(null);
    const handleStartApply = (event, picker) => {
        const start = picker.startDate;
        const end = picker.endDate;

        setStartRange({ startDate: start, endDate: end });

        setStartLabel(
            `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
        );
    };

    const handleStartCancel = () => {
        setStartRange({ startDate: null, endDate: null });
        setStartLabel('');
    };

    const handleWeekApply = (event, picker) => {
        const start = picker.startDate;
        const end = picker.endDate;

        // ✅ Saturday validation
        if (end.day() !== 6) {
            toast.error('End date must be Saturday');
            return;
        }

        setWeekRange({ startDate: start, endDate: end });

        setWeekLabel(
            `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
        );
    };

    const handleWeekCancel = () => {
        setWeekRange({ startDate: null, endDate: null });
        setWeekLabel('');
    };

    // Handlers
    const handleNo = () => {
        setWeekEndingDate(getPriorSaturday().format('MM/DD/YYYY'));
        setDateError('');
        confirmModalCloseRef.current?.click();
    };

    const handleYes = async () => {
        const end = moment(weekEndingDate, 'MM/DD/YYYY', true);
        const start = moment(startDate, 'MM/DD/YYYY', true);

        if (!end.isValid() || !start.isValid()) {
            setDateError('Invalid date format');
            return;
        }

        if (end.isAfter(moment(), 'day')) {
            setDateError('Week-ending date cannot be future');
            return;
        }

        if (start.isAfter(end)) {
            setDateError('Start date cannot be after end date');
            return;
        }

        setDateError('');
        setIsProcessing(true);
        confirmModalCloseRef.current?.click();

        try {
            const response = await post('process-returns', {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD'),
            });

            if (response?.status) {
                const result = {
                    processedAt: moment(),
                    total: response?.data?.total || 0,
                    returned: response?.data?.returned || 0,
                    notReturned: response?.data?.notReturned || 0,
                };

                setHistory(prev => [result, ...prev]);

                toast.success('Returns processed successfully');
            } else {
                toast.error('Processing failed');
            }

        } catch (err) {
            toast.error('Something went wrong');
        } finally {
            setIsProcessing(false);
        }
    };

    // Pagination
    const totalPages = Math.ceil(history.length / itemsPerPage);

    const paginatedHistory = history.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    return (
        <Container>
            <div className="nk-content">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">

                            {/* Header */}
                            <div className="nk-block-head nk-block-head-sm pbZeroHeadin">
                                <div className="nk-block-between">
                                    <div className="nk-block-head-content">
                                        <h3 className="nk-block-title page-title">Process Returns</h3>
                                    </div>

                                    {profile?.name === 'Admin' && (
                                        <div className="nk-block-head-content">
                                            <button
                                                className="btn btn-primary"
                                                data-bs-toggle="modal"
                                                data-bs-target="#processReturnsModal"
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? 'Processing...' : 'Process Returns'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Table Card */}
                            <div className="card card-preview">
                                <div className="card-inner">

                                    <h6 className="mb-3">Processing History</h6>
                                    <p className="text-soft mb-3">
                                        Display a history of return processing under the menu:
                                    </p>

                                    <div style={{ border: '1px solid #000' }}>
                                        {paginatedHistory.map((entry, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    borderBottom: idx !== paginatedHistory.length - 1 ? '1px solid #000' : 'none'
                                                }}
                                            >

                                                {/* LEFT COLUMN (DATE) */}
                                                <div style={{
                                                    width: '260px',
                                                    padding: '10px',
                                                    borderRight: '1px solid #000'
                                                }}>
                                                    {entry.processedAt.format('MMMM D, YYYY [at] h:mm a')}
                                                </div>

                                                {/* RIGHT COLUMN (SUMMARY) */}
                                                <div style={{ flex: 1, padding: '10px' }}>
                                                    <strong>Summary:</strong>

                                                    <ul style={{ marginTop: '8px', padding: 0, listStyle: 'none' }}>

                                                        {/* <li style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                                            <span>Total orders processed</span>
                                                            <span>{entry.total}</span>
                                                        </li> */}

                                                        <li style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                                            <span>Orders marked as Returned</span>
                                                            <span>{entry.returned}</span>
                                                        </li>

                                                        <li style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                                            <span>Orders marked as Not Returned</span>
                                                            <span>{entry.notReturned}</span>
                                                        </li>
                                                    </ul>
                                                </div>

                                            </div>
                                        ))}
                                    </div>

                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <div className="modal fade" id="processReturnsModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Process Returns</h5>
                            <button className="close" data-bs-dismiss="modal" ref={confirmModalCloseRef}>
                                ×
                            </button>
                        </div>

                        <div className="modal-body">

                            <p className="mb-3">
                                Do you want to process returns for the date range below?
                            </p>

                            {/* START DATE */}
                            <div className="mb-3">
                                <label>Start Date Range</label>

                                <DateRangePicker
                                    initialSettings={{
                                        startDate: startRange.startDate || moment().subtract(10, 'months'),
                                        endDate: startRange.endDate || moment(),
                                        locale: {
                                            format: 'MMM D, YYYY',
                                            cancelLabel: 'Clear',
                                            applyLabel: 'Apply',
                                        },
                                        // ranges: {
                                        //     'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                                        //     'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                                        //     'Last 10 Months': [moment().subtract(10, 'months'), moment()],
                                        // },
                                        opens: 'center',
                                        autoApply: true,
                                        alwaysShowCalendars: true,
                                    }}
                                    onApply={handleStartApply}
                                    onCancel={handleStartCancel}
                                >
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Select start date range"
                                        value={startLabel}
                                        readOnly
                                    />
                                </DateRangePicker>
                            </div>

                            {/* END DATE (Saturday) */}
                            <div className="mb-3">
                                <label>Week-ending (Saturday)</label>

                                <DateRangePicker
                                    initialSettings={{
                                        startDate: weekRange.startDate || moment().subtract(6, 'days'),
                                        endDate: weekRange.endDate || getPriorSaturday(),
                                        locale: {
                                            format: 'MMM D, YYYY',
                                            cancelLabel: 'Clear',
                                            applyLabel: 'Apply',
                                        },
                                        opens: 'center',
                                        drops: 'down', // ✅ ensures it opens below
                                        autoApply: true,
                                        alwaysShowCalendars: true,
                                    }}
                                    onApply={(event, picker) => {
                                        const start = picker.startDate;
                                        const end = picker.endDate;

                                        // ✅ enforce Saturday
                                        if (end.day() !== 6) {
                                            toast.error('End date must be Saturday');
                                            return;
                                        }

                                        setWeekRange({ startDate: start, endDate: end });

                                        setWeekLabel(
                                            `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
                                        );

                                        // also update main weekEndingDate state
                                        setWeekEndingDate(end.format('MM/DD/YYYY'));
                                    }}
                                    onCancel={() => {
                                        setWeekRange({ startDate: null, endDate: null });
                                        setWeekLabel('');
                                    }}
                                >
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Select week range (end must be Saturday)"
                                        value={weekLabel}
                                        readOnly
                                    />
                                </DateRangePicker>
                            </div>

                            {dateError && (
                                <div className="text-danger">{dateError}</div>
                            )}

                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={handleNo}>
                                No
                            </button>

                            <button className="btn btn-primary" onClick={handleYes}>
                                {isProcessing ? 'Processing...' : 'Yes'}
                            </button>
                        </div>

                    </div>
                </div>
            </div>

        </Container>
    );
}

export default ProcessReturns;